import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Send, Loader2, Trash2, Clock, FileSearch } from "lucide-react";
import { useNavigate } from "react-router-dom";
import CreateRcaDialog from "@/components/rca/CreateRcaDialog";
import { PRIORITY, TICKET_STATUS, initials, timeAgo } from "@/lib/ui";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function TicketDrawer({ ticketId, engineers = [], onClose, onChanged }) {
  const { user } = useAuth();
  const [t, setT] = useState(null);
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const isClient = user?.role === "client";
  const navigate = useNavigate();
  const [rcas, setRcas] = useState([]);
  const [creatingRca, setCreatingRca] = useState(false);
  useEffect(() => {
    if (!isClient) api.get("/rcas", { params: { ticket_id: ticketId } }).then((r) => setRcas(r.data)).catch(() => {});
  }, [ticketId, isClient]);

  const load = async () => {
    const { data } = await api.get(`/tickets/${ticketId}`);
    setT(data);
  };
  useEffect(() => { load(); }, [ticketId]);

  const patch = async (updates) => {
    await api.patch(`/tickets/${ticketId}`, updates);
    await load();
    onChanged?.();
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    setSending(true);
    try {
      await api.post("/notes", { target_type: "ticket", target_id: ticketId, content: comment });
      setComment("");
      await load();
      toast.success("Comment posted");
    } catch { toast.error("Could not post comment"); }
    finally { setSending(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this ticket?")) return;
    await api.delete(`/tickets/${ticketId}`);
    toast.success("Ticket deleted");
    onChanged?.(); onClose();
  };

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-[560px] overflow-y-auto scroll-thin p-0" data-testid="ticket-detail-drawer">
        {!t ? (
          <div className="p-8 text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
        ) : (
          <div>
            <SheetHeader className="p-6 pb-4 border-b border-slate-100">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-mono text-xs font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">{t.key}</span>
                <Badge variant="outline" className={`h-5 px-1.5 text-[10px] ${TICKET_STATUS[t.status]?.chip}`}>{TICKET_STATUS[t.status]?.label}</Badge>
                <span className="text-[11px] text-slate-400 inline-flex items-center gap-1"><Clock className="w-3 h-3" />{t.age_days}d old · {t.days_in_status}d in status</span>
              </div>
              <SheetTitle className="font-display text-xl font-bold text-slate-900 text-left pr-8">{t.title}</SheetTitle>
            </SheetHeader>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">Status</label>
                  {isClient ? (
                    <div><Badge variant="outline" className={`${TICKET_STATUS[t.status]?.chip}`}>{TICKET_STATUS[t.status]?.label}</Badge></div>
                  ) : (
                    <Select value={t.status} onValueChange={(v) => patch({ status: v })}>
                      <SelectTrigger className="h-9" data-testid="ticket-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(TICKET_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">Priority</label>
                  {isClient ? (
                    <div><Badge variant="outline" className={PRIORITY[t.priority]?.badge}>{PRIORITY[t.priority]?.label}</Badge></div>
                  ) : (
                    <Select value={t.priority} onValueChange={(v) => patch({ priority: v })}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(PRIORITY).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </div>
                {!isClient && (
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">Assignee</label>
                    <Select value={t.assignee_id || "none"} onValueChange={(v) => patch({ assignee_id: v === "none" ? null : v })}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Unassigned</SelectItem>
                        {engineers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">Client</label>
                  <p className="text-sm text-slate-700 pt-2">{t.client_name || "—"}</p>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">Description</label>
                <p className="text-sm text-slate-600 whitespace-pre-wrap">{t.description || "No description."}</p>
                {t.task_title && <p className="text-xs text-slate-400 mt-1">Linked task: {t.task_title}</p>}
              </div>

              {!isClient && (
                <div className="space-y-1.5" data-testid="ticket-rca-section">
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">Root cause analysis</label>
                  <div className="flex flex-wrap gap-2 items-center">
                    {rcas.map((r) => (
                      <button key={r.id} onClick={() => navigate(`/rca/${r.id}`)} className="text-xs font-mono font-semibold px-2 py-1 rounded bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100" data-testid={`ticket-rca-link-${r.id}`}>{r.key} · {r.status.replace("_", " ")}</button>
                    ))}
                    <Button size="sm" variant="outline" onClick={() => setCreatingRca(true)} data-testid="ticket-create-rca-button"><FileSearch className="w-3.5 h-3.5 mr-1.5" />Create RCA</Button>
                  </div>
                </div>
              )}

              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2 block">Conversation</label>
                <div className="space-y-3 mb-3">
                  {t.comments?.map((c) => (
                    <div key={c.id} className="flex items-start gap-2.5" data-testid={`ticket-comment-${c.id}`}>
                      <Avatar className="w-7 h-7"><AvatarImage src={c.author_avatar} /><AvatarFallback className="bg-indigo-100 text-indigo-700 text-[10px]">{initials(c.author_name)}</AvatarFallback></Avatar>
                      <div className="flex-1 bg-slate-50 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-1.5 text-xs"><span className="font-semibold text-slate-700">{c.author_name}</span><span className="text-slate-400">· {timeAgo(c.created_at)}</span></div>
                        <p className="text-sm text-slate-600 mt-0.5 whitespace-pre-wrap">{c.content}</p>
                      </div>
                    </div>
                  ))}
                  {(!t.comments || t.comments.length === 0) && <p className="text-xs text-slate-400">No replies yet.</p>}
                </div>
                <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Write a reply…" className="text-sm min-h-[64px]" data-testid="ticket-comment-input" />
                <Button size="sm" className="mt-2 bg-indigo-600 hover:bg-indigo-700" onClick={addComment} disabled={sending} data-testid="ticket-comment-submit">
                  {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Send className="w-3.5 h-3.5 mr-1.5" />} Reply
                </Button>
              </div>

              {user?.role === "manager" && (
                <div className="pt-4 border-t border-slate-100">
                  <Button variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={remove} data-testid="ticket-delete-button">
                    <Trash2 className="w-4 h-4 mr-1.5" /> Delete ticket
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </SheetContent>
      {creatingRca && t && <CreateRcaDialog ticket={t} onClose={() => setCreatingRca(false)} />}
    </Sheet>
  );
}
